import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";
import { ensureUserDoc } from "../lib/users";
import { ensureStripeCustomer } from "../lib/stripeCustomer";

const AuthContext = createContext({
  user: null,
  profile: null,
  account: null,
  accountRole: null,
  isAccountOwner: false,
  canAct: false,
  loading: true,
  logout: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [account, setAccount] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setAuthLoading(false);
      if (!u) {
        setProfile(null);
        setAccount(null);
        return;
      }

      // Signup side-effect — seed the profile doc on first sign-in. Idempotent,
      // so running it on every sign-in is safe. (Stripe is provisioned only for
      // account owners, in a dedicated effect below — not for every member.)
      try {
        await ensureUserDoc(u);
      } catch (e) {
        console.warn("[auth] ensureUserDoc failed", e);
      }
    });
    return unsub;
  }, []);

  // Live profile.
  useEffect(() => {
    if (!user) return;
    setProfileLoading(true);
    const unsub = onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        setProfile(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setProfileLoading(false);
      },
      () => setProfileLoading(false)
    );
    return unsub;
  }, [user]);

  // Live account (when the user belongs to one). The account doc holds the
  // authoritative roles map used to gate acting vs. viewing across the app.
  useEffect(() => {
    const accountId = profile?.accountId;
    if (!accountId) {
      setAccount(null);
      return;
    }
    const unsub = onSnapshot(
      doc(db, "accounts", accountId),
      (snap) => setAccount(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      () => setAccount(null)
    );
    return unsub;
  }, [profile?.accountId]);

  const accountRole = account && user ? account.roles?.[user.uid] || null : null;
  const isAccountOwner = accountRole === "owner";
  const isAdmin = !!profile?.isAdmin || ["admin", "superAdmin"].includes(profile?.role);
  // Who may WRITE (send messages, log bugs, submit requests): owners, editors,
  // and studio admins. Viewers are read-only.
  const canAct = isAdmin || accountRole === "owner" || accountRole === "editor";

  // Provision a Stripe customer ONLY for the account owner (billing anchor).
  // Non-owner members and account-less users never spawn a customer.
  useEffect(() => {
    if (!user || !isAccountOwner) return;
    if (profile?.stripeCustomerId) return;
    ensureStripeCustomer({ user, profile }).catch((e) =>
      console.warn("[auth] ensureStripeCustomer failed", e)
    );
  }, [user, isAccountOwner, profile]);

  const value = {
    user,
    profile,
    account,
    accountRole,
    isAccountOwner,
    canAct,
    loading: authLoading || (!!user && profileLoading && !profile),
    logout: () => signOut(auth),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
